<template>
  <div class="container">
    <md-content v-if="caseConf" class="case-preview md-scrollbar gray-3 bg">
      <div class="header">
        <h3 class="case-name">{{ caseConf.name }}</h3>
        <div>
          <help-tooltip v-if="caseConf.warningsCount" type="warning">{{ caseConf.warningsCount }} warning(s) found.</help-tooltip>
          <help-tooltip v-if="caseConf.errorsCount" type="error">{{ caseConf.errorsCount }} error(s) found.</help-tooltip>
        </div>
        <md-button v-if="showFavorite" class="md-primary md-icon-button favorite-button">
          <md-icon class="favorite-icon" :class="{ active: caseConf.isFavorite }" label="Favorite">star</md-icon>
        </md-button>
      </div>
      
      <div class="summary">
        <div class="image-container">
          <div class="avatar image" :style="{'background-image': `url(${pictureFor(caseConf._avatar)})`}" />
          <span class="caption">{{ get(caseConf, '_avatar.name.en') }}</span>
        </div>
        <div class="image-container">
          <div class="environment image" :style="{'background-image': `url(${pictureFor(caseConf._startLocation)})`}" />
          <span class="caption">{{ get(caseConf, '_startLocation.name.en') }}</span>
        </div>
        <div v-if="caseConf._endLocation" class="image-container">
          <div class="environment image" :style="{'background-image': `url(${pictureFor(caseConf._endLocation)})`}" />
          <span class="caption">{{ get(caseConf, '_endLocation.name.en') }}</span>
        </div>
      </div>

      <div v-if="showTags" class="tags">
        <md-chip v-if="!caseConf.tags || caseConf.tags.length === 0" class="md-primary tag gray-2 bg">No tags</md-chip>
        <md-chip v-for="(tag, index) in caseConf.tags" :key="index" class="md-primary tag gray-6 bg">
          {{ tag.text }}
        </md-chip>
      </div>

      <div class="dispatch">
        <h4 class="title">
          {{ 'Learning Objectives' | translateFeatureLabel }}
        </h4>
        <p class="content">{{ objectiveText }}</p>
        <div class="expander-line">
          <a v-if="showObjectiveExpander" class="expander" @click="expandObjective()">View More</a>
        </div>
        <h4 class="title">Scenario Information</h4>
        <p class="content">{{ dispatchText }}</p>
        <div class="expander-line">
          <a v-if="showDispatchExpander" class="expander" @click="expandDispatch()">View More</a>
        </div>
      </div>

      <div class="buttons">
        <slot name="actions" />
      </div>
    </md-content>
  </div>
</template>

<script>
import get from 'lodash/get';

import HelpTooltip from '~lib/common/components/HelpTooltip';

const MAX_NUM_WORDS = 20;

export default {
  name: 'case-preview',
  components: {
    HelpTooltip,
  },
  props: {
    caseConf: {
      type: Object,
      required: true,
    },
    user: {
      type: Object,
      required: true,
    },
    showFavorite: {
      type: Boolean,
      default: true,
    },
    showTags: {
      type: Boolean,
      default: true,
    },
    isCompact: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      state: {
        objectiveExpanded: false,
        dispatchExpanded: false,
      },
      defaultPicture: 'http://icons.iconarchive.com/icons/papirus-team/papirus-status/128/image-missing-icon.png',
    };
  },
  computed: {
    showObjectiveExpander() {
      if (this.state.objectiveExpanded || !this.isCompact) {
        return false;
      } else {
        const objective = get(this.caseConf, 'dispatch.learningObjectives.caption.en', '');
        return objective.split(' ').length > MAX_NUM_WORDS;
      }
    },
    showDispatchExpander() {
      if (this.state.dispatchExpanded || !this.isCompact) {
        return false;
      } else {
        const dispatch = get(this.caseConf, 'dispatch.chiefComplaint.caption.en', '');
        return dispatch.split(' ').length > MAX_NUM_WORDS;
      }
    },
    objectiveText() {
      const objective = get(this.caseConf, 'dispatch.learningObjectives.caption.en', '');
      if (this.state.objectiveExpanded || !this.isCompact) {
        return objective;
      } else {
        return objective.split(' ').splice(0, MAX_NUM_WORDS).join(' ');
      }
    },
    dispatchText() {      
      const dispatch = get(this.caseConf, 'dispatch.chiefComplaint.caption.en', '');
      if (this.state.dispatchExpanded || !this.isCompact) {
        return dispatch;
      } else {
        return dispatch.split(' ').splice(0, MAX_NUM_WORDS).join(' ');
      }
    }
  },
  watch: {
    caseConf(oldValue, newValue) {
      if (oldValue != newValue) {
        this.state.objectiveExpanded = false;
        this.state.dispatchExpanded = false;
      }
    }
  },
  methods: {
    load() {
      this.$emit('load', this.caseConf);
    },
    addToTemplates() {
      this.$emit('addToTemplates', this.caseConf);
    },
    pictureFor(item) {
      return get(item, 'picture.url', this.defaultPicture);
    },    
    update(updates) {
      this.$emit('update', updates);
    },
    get,
    expandDispatch() {
      this.state.dispatchExpanded = true;
    },
    expandObjective() {
      this.state.objectiveExpanded = true;
    }
  },
};
</script>

<style scoped>
.container {
  min-width: 400px;
  width: 30%;
}
.case-preview {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: auto;
}

.header, .summary, .tags {
  padding: 10px 0;
  border-bottom: solid 4px #004580;
}

.header {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.case-name, .caption {
  font-style: normal;
  font-weight: 500;
  font-size: 28px;
  line-height: 1.2;
}

.case-name {
  margin: 0 auto 0 0;
  flex: 1 0 0;
}

.summary {
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
  flex-wrap: nowrap;
  /* for the captions */
}

.image-container {
  width: 100px;
  text-align: center;
  margin: 10px;
  position: relative;
  flex-grow: 1;
  display: flex;
  align-items: center;
  flex-direction: column;
}
.image-container .image {
  width: 100px;
  height: 100px;
  background-size: auto 100%;
  background-repeat: no-repeat;
  box-shadow: 1px 1px 4px 0 rgba(0,0,0,.4);
}

.image-container:first-child {
  margin-left: 0;
}

.image-container:last-child {
  margin-right: 0;
}

.avatar {
  background-size: cover;
  border-radius: 50%;
}

.environment {
  border-radius: 15px;
}

.image-container .caption {
  display: block;
  margin-top: 5px;
  position: relative;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
}

@media (min-width: 1200px) {
  .image-container .caption {
    font-size: 16px;
  }
}

@media (min-width: 1500px) {
  .image-container .caption {
    font-size: 18px;
  }
}

@media (min-width: 1800px) {
  .image-container .caption {
    font-size: 20px;
  }
}

.md-chip.tag {
  margin: 2px;
}
.tags {
  flex: 1 0 auto;
  max-height: fit-content;
}

.dispatch .title {
  font-style: normal;
  font-weight: 500;
  font-size: 18px;
  line-height: 20px;
  text-transform: uppercase;
  margin: 0;
  margin-top: 20px;
}

.dispatch .content {
  font-style: normal;
  font-weight: normal;
  font-size: 18px;
  line-height: 20px;
  margin: 8px 0;
  white-space: pre-wrap;
}

.buttons {
  flex-direction: row;
  justify-content: flex-end;
  border-bottom: none;
  float: right;
  margin-top: 15px;
}
.buttons .md-button {
  text-transform: uppercase;
}

.md-button.duplicate {
  color: white !important;
  background-color: #face14 !important;
}

.md-button:first-child {
  margin-left: 0;
}

.favorite-button {
  min-width: unset;
  border-radius: 50%;
}
.favorite-icon {
  color: #5F5F5F !important;
  font-size: 24px !important;
}
.favorite-icon.active {
  color: #FFB800 !important;
}
.expander-line {
  text-align: center;
}
.expander {
  cursor: pointer;
}
</style>

<style>
.case-preview .buttons .md-button-content {
  display: flex;
  justify-content: space-evenly;
  align-items: center;
  width: 100%;
  height: 100%;
}
</style>